import React, { useState, useEffect } from "react";
import { useSocials } from "../../../hooks/useSocials";
import { useSocialCreate } from "../../../hooks/useSocialCreate";
import { useSocialDelete } from "../../../hooks/useSocialDelete";

interface SocialsTabProps {
  // Props específicas se necessário
}

const SocialsTab: React.FC<SocialsTabProps> = () => {
  // Estados locais para a aba de socials
  const [socialForm, setSocialForm] = useState({
    type: "",
    url: "",
  });
  const [currentSocialPage, setCurrentSocialPage] = useState(1);
  const socialPageSize = 10;

  // Hooks específicos para socials
  const { fetchSocials, socialState } = useSocials();
  const { createSocial, createState: socialCreateState } = useSocialCreate();
  const { deleteSocial, deleteState: socialDeleteState } = useSocialDelete();

  // Buscar redes sociais ao montar o componente
  useEffect(() => {
    fetchSocials();
  }, [fetchSocials]);

  // Funções específicas para socials
  const handleCreateSocial = async () => {
    if (!socialForm.type || !socialForm.url) return;

    const success = await createSocial({
      type: socialForm.type,
      url: socialForm.url,
    });

    if (success) {
      setSocialForm({ type: "", url: "" });
      fetchSocials();
    }
  };

  const handleDeleteSocial = async (socialId: string) => {
    if (window.confirm("Tem certeza que deseja deletar esta rede social?")) {
      const success = await deleteSocial(socialId);
      if (success) {
        fetchSocials();

        // Ajustar página atual se necessário
        const newTotalPages = Math.ceil(
          (socialState.socials.length - 1) / socialPageSize
        );
        if (currentSocialPage > newTotalPages && newTotalPages > 0) {
          setCurrentSocialPage(newTotalPages);
        }
      }
    }
  };

  // Paginação
  const totalSocialPages = Math.ceil(
    socialState.socials.length / socialPageSize
  );
  const getPaginatedSocials = () => {
    const startIndex = (currentSocialPage - 1) * socialPageSize;
    const endIndex = startIndex + socialPageSize;
    return socialState.socials.slice(startIndex, endIndex);
  };

  // Loading state
  if (socialState.isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <span>Carregando redes sociais...</span>
      </div>
    );
  }

  // Error state
  if (socialState.error) {
    return <div className="error-message">{socialState.error}</div>;
  }

  return (
    <div className="socials-container">
      {/* Formulário para criar social */}
      <div className="social-form-section">
        <h3>📱 Criar Nova Rede Social</h3>
        <div className="form-group">
          <select
            value={socialForm.type}
            onChange={(e) =>
              setSocialForm((prev) => ({
                ...prev,
                type: e.target.value,
              }))
            }
          >
            <option value="">Selecione o tipo</option>
            <option value="email">✉️ Email</option>
            <option value="phone">📞 Telefone</option>
            <option value="website">🌐 Website</option>
            <option value="instagram">📷 Instagram</option>
            <option value="facebook">👥 Facebook</option>
            <option value="whatsapp">💬 WhatsApp</option>
            <option value="x">🐦 X (Twitter)</option>
          </select>
        </div>
        <div className="form-group">
          <input
            type="url"
            placeholder="URL da rede social"
            value={socialForm.url}
            onChange={(e) =>
              setSocialForm((prev) => ({
                ...prev,
                url: e.target.value,
              }))
            }
          />
        </div>
        <button
          className="create-social-button"
          onClick={handleCreateSocial}
          disabled={
            !socialForm.type || !socialForm.url || socialCreateState.isLoading
          }
        >
          {socialCreateState.isLoading ? "Criando..." : "Criar Rede Social"}
        </button>

        {socialCreateState.error && (
          <div className="error-message">❌ {socialCreateState.error}</div>
        )}

        {socialCreateState.success && (
          <div className="success-message">
            ✅ Rede social criada com sucesso!
          </div>
        )}
      </div>

      {/* Lista de Redes Sociais */}
      <div className="socials-list-section">
        <h3>📋 Redes Sociais</h3>

        {socialState.socials.length === 0 ? (
          <p>Nenhuma rede social encontrada.</p>
        ) : (
          <div className="socials-table-container">
            <table className="socials-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>URL</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedSocials().map((social) => (
                  <tr key={social.id} className="social-row">
                    <td className="social-type">
                      {social.type === "email" && "✉️ Email"}
                      {social.type === "phone" && "📞 Telefone"}
                      {social.type === "website" && "🌐 Website"}
                      {social.type === "instagram" && "📷 Instagram"}
                      {social.type === "facebook" && "👥 Facebook"}
                      {social.type === "whatsapp" && "💬 WhatsApp"}
                      {social.type === "x" && "🐦 X (Twitter)"}
                    </td>
                    <td className="social-url">
                      <a
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                      >
                        {social.url}
                      </a>
                    </td>
                    <td className="social-date">
                      {new Date(social.createdAt).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="social-actions">
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteSocial(social.id)}
                        disabled={socialDeleteState.isLoading}
                      >
                        🗑️ Deletar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginação */}
            {totalSocialPages > 1 && (
              <div className="socials-pagination">
                <button
                  onClick={() =>
                    setCurrentSocialPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentSocialPage === 1}
                  className="pagination-button"
                >
                  ← Anterior
                </button>
                <span className="pagination-info">
                  Página {currentSocialPage} de {totalSocialPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentSocialPage((prev) =>
                      Math.min(prev + 1, totalSocialPages)
                    )
                  }
                  disabled={currentSocialPage === totalSocialPages}
                  className="pagination-button"
                >
                  Próximo →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialsTab;
